import { fetchUtils } from "react-admin";
import { stringify } from "query-string";

const apiUrl=process.env.REACT_APP_BASE_API_URL;
export const httpClient = (url, options = {}) => {
  if (!options.headers) {
    options.headers = new Headers({ Accept: "application/json" });
  }

  // add my own headers here
  // options.headers.set("Access-Control-Expose-Headers", "Content-Range");
  // options.headers.set("Content-Range", "users 0-24/319");
  const { accessToken } = JSON.parse(localStorage.getItem("auth"));
  options.headers.set("Authorization", `Bearer ${accessToken}`);
  return fetchUtils.fetchJson(url, options);
};

// Cache for warehouse names to avoid repeated API calls
let warehouseNamesCache = null;
let warehouseCacheTimestamp = null;
const CACHE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

// Function to fetch warehouse names from API
const fetchWarehouseNames = async () => {
  // Check if we have a valid cache
  const now = new Date().getTime();
  if (warehouseNamesCache && warehouseCacheTimestamp && (now - warehouseCacheTimestamp < CACHE_EXPIRY_MS)) {
    return warehouseNamesCache;
  }
  
  try {
    const response = await httpClient(`${apiUrl}/wms/api/warehouses`);
    console.log("Warehouse API response:", response.json);
    
    // Extract warehouse names from the response
    // Handle different possible response structures
    let warehouses = [];
    
    if (Array.isArray(response.json)) {
      // If response is an array, extract names directly
      warehouses = response.json.map(warehouse => 
        typeof warehouse === 'object' && warehouse.name ? warehouse.name : warehouse.toString()
      );
    } else if (typeof response.json === 'object' && response.json !== null) {
      // If response is an object, try to extract data from common API response patterns
      if (Array.isArray(response.json.data)) {
        warehouses = response.json.data.map(warehouse => 
          typeof warehouse === 'object' && warehouse.name ? warehouse.name : warehouse.toString()
        );
      } else if (Array.isArray(response.json.warehouses)) {
        warehouses = response.json.warehouses.map(warehouse => 
          typeof warehouse === 'object' && warehouse.name ? warehouse.name : warehouse.toString()
        );
      } else if (Array.isArray(response.json.results)) {
        warehouses = response.json.results.map(warehouse => 
          typeof warehouse === 'object' && warehouse.name ? warehouse.name : warehouse.toString()
        );
      } else {
        // If we can't find an array in the response, try to extract warehouse names from object keys
        warehouses = Object.keys(response.json).filter(key => 
          key !== 'status' && key !== 'message' && key !== 'error'
        );
      }
    }
    
    // If we couldn't extract any warehouses, fall back to defaults
    if (warehouses.length === 0) {
      warehouses = ["Tardeo", "Goregaon", "Thane", "NaviMumbai", "Others"];
      console.warn("Could not extract warehouse names from API response, using defaults");
    }
    
    console.log("Extracted warehouses:", warehouses);
    
    // Update cache
    warehouseNamesCache = warehouses;
    warehouseCacheTimestamp = now;
    
    return warehouses;
  } catch (error) {
    console.error("Failed to fetch warehouses:", error);
    // Return default warehouses if API call fails
    return ["Tardeo", "Goregaon", "Thane", "NaviMumbai", "Others"];
  }
};

// Function to build warehouse query string
const buildWarehouseQueryString = (warehouses) => {
  return warehouses.map(warehouse => 
    `where%5Bwarehouse%5D[in]=${encodeURIComponent(warehouse)}`
  ).join('&');
};

// Here is how this Data Provider maps react-admin calls to API calls
export const CustomDataProvider = {
  getList: async (resource, params) => {
    // console.log("params",params.filter.status);
    const { total } = resource;
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;
    
    let localAuth = JSON.parse(localStorage.getItem("auth")) || [];
    let currentRole = localAuth.roles?.[0] || '';
    
    // Fetch warehouse names
    const warehouseNames = await fetchWarehouseNames();
    
    // Generate query parameters
    const query = {
      skip: (page - 1) * perPage,
      take: perPage,
    };
    
    // Base query string without undefined prefix
    let baseQuery = stringify(query);
    
    // REMOVED unused roleToWarehouseMapping and dynamic role handling logic.
    // The filtering logic below now correctly handles
    // both explicit and role-based filtering using getWarehouseFromRole.
    
    // Handle search query filters
    if (params.filter && params.filter.q) {
      let queryValue = params.filter.q.toString(); // Convert to string
      if (!isNaN(queryValue) && Number(queryValue) > 2) {
        baseQuery = `${baseQuery}&where%5Border_number%5D[contains]=${queryValue}`;
      } else {
        // Convert queryValue to lowercase for a case-insensitive search
        queryValue = queryValue.toLowerCase();
        // Capitalize the first letter
        queryValue = queryValue.charAt(0).toUpperCase() + queryValue.substr(1);
        
        baseQuery = `${baseQuery}&where%5Bcustomer_name%5D[contains]=${queryValue}`;
      }
    }

    // status filter
    if (
      params.filter &&
      params.filter.status &&
      params.filter.status.length > 0
    ) {
      baseQuery = `${baseQuery}&${params.filter.status}`;
    }
    
    // warehouse filter
    // Determine the warehouse filter to apply
    let warehouseToFilter = null;
    const userSelectedWarehouse = params.filter?.warehouse;

    if (userSelectedWarehouse && userSelectedWarehouse.length > 0) {
      // 1. Prioritize user's explicit filter selection (expecting just the name)
      warehouseToFilter = userSelectedWarehouse;
      console.log(`Applying explicit warehouse filter: ${warehouseToFilter}`);
    } else if (
      typeof resource === "string" && resource.search("orders") !== -1 &&
      currentRole && currentRole !== 'admin' && currentRole.endsWith('_manager')
    ) {
      // 2. Fallback to role-based filter if no explicit filter is set and user is a manager
      warehouseToFilter = getWarehouseFromRole(currentRole);
      console.log(`Applying role-based warehouse filter for ${currentRole}: ${warehouseToFilter}`);
    }
    // 3. If user is admin and no filter selected, warehouseToFilter remains null (show all)

    // Construct the query parameter if a warehouse filter needs to be applied
    if (warehouseToFilter) {
      let finalWarehouseName = warehouseToFilter;
      // Defensive check: If the value already looks like a param, try to extract the name
      if (typeof finalWarehouseName === 'string' && finalWarehouseName.includes('where%5Bwarehouse%5D[in]=')) {
        console.warn(`Received pre-formatted warehouse filter: ${finalWarehouseName}. Attempting to extract name.`);
        try {
          // Extract the value after the '=' and decode it
          finalWarehouseName = decodeURIComponent(finalWarehouseName.split('=')[1]);
        } catch (e) {
          console.error("Failed to extract warehouse name from pre-formatted string:", e);
          // Fallback or handle error appropriately, maybe skip filtering
          finalWarehouseName = null;
        }
      }
      
      // Only add the filter if we have a valid name
      if (finalWarehouseName) {
        baseQuery = `${baseQuery}&where%5Bwarehouse%5D[in]=${encodeURIComponent(finalWarehouseName)}`;
      }
    }
    
    // date filter
    if (
      params.filter &&
      params.filter.startDate &&
      params.filter.startDate.length > 0
    ) {
      baseQuery = `${baseQuery}&where%5Bprocessed_at%5D[gte]=${params.filter.startDate}`;
    }
    
    if (
      params.filter &&
      params.filter.endDate &&
      params.filter.endDate.length > 0
    ) {
      baseQuery = `${baseQuery}&where%5Bprocessed_at%5D[lte]=${params.filter.endDate}`;
    }

    let url1;
    if (typeof resource === "string" && resource.search("orders") === -1) {
      url1 = `${apiUrl}/${resource}?${baseQuery}&orderBy%5BcreatedAt%5D=desc`;
    } else {
      url1 = `${apiUrl}/${resource}?${baseQuery}&orderBy%5Bprocessed_at%5D=desc`;
    }
    return httpClient(url1).then((response) => {
      const length = response.json?.length;
      const total = resource === "api/orders" ? 1000000 : length;
      return { data: response.json, total };
    });
  },

  getOne: (resource, params) =>
    httpClient(`${apiUrl}/${resource}/${params.id}`).then(({ json }) => ({
      data: json,
    })),

  getMany: (resource, params) => {
    let queryString = "";
    if (params && params.ids && params.ids.length > 0) {
      for (let i = 0; i < params.ids[0].length; i++) {
        // console.log("Before Log",params.ids[0][i]);
        let q = `where%5Bid%5D[in]=${params.ids[0][i]}`;
        queryString = queryString + q;
        if (i !== params.ids[0].length - 1) {
          queryString = queryString + "&";
        }
      }
      queryString = queryString + "&orderBy%5Bprocessed_at%5D=desc";
    }

    // const url = `${apiUrl}/${resource}?where%5Bid%5D[in]=claflcglw01128upakyy7djur&where%5Bid%5D[in]=claflbin800988upabefyvtyl&where%5Bid%5D[in]=clafl8g5000358upas7y8llfb`;
    const url = `${apiUrl}/${resource}?${queryString}`;
    return httpClient(url).then(({ json }) => ({ data: json }));
  },

  update: (resource, params) =>
    httpClient(`${apiUrl}/${resource}/${params.id}`, {
      method: "PATCH",
      body: JSON.stringify(params.data),
    }).then(({ json }) => ({ data: json, total: json?.length })),

  create: (resource, params) =>
    httpClient(`${apiUrl}/${resource}`, {
      method: "POST",
      body: JSON.stringify(params.data),
    }).then(({ json }) => ({
      data: { ...params.data, id: json.id },
    })),

  delete: (resource, params) =>
    httpClient(`${apiUrl}/${resource}/${params.id}`, {
      method: "DELETE",
    }).then(({ json }) => ({ data: json })),

  deleteMany: (resource, params) => {
    return httpClient(`${apiUrl}/${resource}?${params.id}`, {
      method: "DELETE",
      body: JSON.stringify(params.data),
    }).then(({ json }) => ({ data: json }));
  },
};

// Helper function to get warehouse from role
const getWarehouseFromRole = (role) => {
  const warehouseName = role.replace('_manager', '');
  // Apply the same case-insensitive check here
  if (warehouseName.toLowerCase() === 'navimumbai') {
    return 'NaviMumbai'; // Ensure correct casing
  }
  // Default capitalization for other warehouses
  const formattedWarehouse = warehouseName.charAt(0).toUpperCase() + warehouseName.slice(1);
  return formattedWarehouse;
};
